import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, Paper, Divider, Chip, CircularProgress, Alert } from '@mui/material';
import { CheckCircleOutline, Email, ContentCopy, VerifiedUser, ReceiptLong, Send as SendIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { invokeApi, apiList } from '../../services/apiServices';
import { InvoiceModal, PURPLE } from '../common/InvoiceDocument';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderId, setOrderId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Paid');
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [verifiedOrder, setVerifiedOrder] = useState(location.state?.order || null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailNotice, setEmailNotice] = useState('');

  // Retrieve any state passed from checkout
  const orderDetails = location.state || {};

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryOrderId = queryParams.get('order_id') || queryParams.get('orderId');

    const activeOrderId = queryOrderId || location.state?.orderId || location.state?.order_number || ('ORD-' + Math.floor(100000 + Math.random() * 900000));
    setOrderId(activeOrderId);

    // If redirected from Cashfree with order_id, verify payment with backend
    if (queryOrderId) {
      setIsVerifying(true);
      invokeApi(apiList.verifyCashfreePayment, { order_id: queryOrderId })
        .then((res) => {
          if (res?.data?.is_paid || res?.data?.order_status === 'PAID' || res?.data?.order_status === 'SUCCESS') {
            setPaymentStatus('Paid');
          } else if (res?.data?.order_status) {
            setPaymentStatus(res.data.order_status);
          }
          if (res?.data?.order) {
            setVerifiedOrder(res.data.order);
            try {
              const dashOrders = JSON.parse(localStorage.getItem('ar_dashboard_orders') || '[]');
              const existingIdx = dashOrders.findIndex(o => String(o.order_number || o.id) === String(res.data.order.order_number || res.data.order.id));
              if (existingIdx !== -1) {
                dashOrders[existingIdx] = { ...dashOrders[existingIdx], ...res.data.order, payment_status: 'Paid' };
              } else {
                dashOrders.unshift(res.data.order);
              }
              localStorage.setItem('ar_dashboard_orders', JSON.stringify(dashOrders));
              try {
                window.dispatchEvent(new Event('storage'));
                window.dispatchEvent(new CustomEvent('ar_orders_updated'));
              } catch (e) {}
            } catch (e) {}
          }
        })
        .catch((err) => {
          console.error('Error verifying Cashfree payment:', err);
        })
        .finally(() => {
          setIsVerifying(false);
        });
    }
  }, [location]);

  const handleCopy = () => {
    if (orderId) {
      navigator.clipboard.writeText(orderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleResendEmail = async () => {
    const activeId = verifiedOrder?.order_number || orderId;
    if (!activeId) return;
    setResendingEmail(true);
    setEmailNotice('');
    try {
      const res = await invokeApi(apiList.resendOrderEmail, { order_id: activeId });
      if (res?.status === 200 || res?.data?.responseCode === '200') {
        setEmailNotice('✓ Confirmation email dispatched successfully! Please check your inbox and spam folder.');
      } else {
        setEmailNotice(res?.data?.responseMessage || 'Email could not be resent at this moment.');
      }
    } catch {
      setEmailNotice('Email request completed. Please check your inbox shortly.');
    } finally {
      setResendingEmail(false);
    }
  };

  // Build complete verified invoice data
  const activeOrder = verifiedOrder || orderDetails || {};
  const rawItems = (activeOrder.items && activeOrder.items.length > 0)
    ? activeOrder.items
    : (activeOrder.order_items && activeOrder.order_items.length > 0 ? activeOrder.order_items : []);

  const totalAmount = parseFloat(activeOrder.total_amount || activeOrder.totalAmount || activeOrder.total || orderDetails.totalAmount || 0);

  const invoiceData = {
    invoice_number: activeOrder.order_number || orderId,
    customer_name: activeOrder.customer_name || activeOrder.customerName || activeOrder.fullName || orderDetails.customerName || orderDetails.fullName || 'Valued Customer',
    company_name: activeOrder.company_name || activeOrder.companyName || orderDetails.companyName || '',
    customer_number: activeOrder.customer_phone || activeOrder.customerPhone || orderDetails.phoneNumber || orderDetails.phone || '',
    customer_email: activeOrder.customer_email || activeOrder.customerEmail || orderDetails.email || '',
    gst_number: activeOrder.gst_number || activeOrder.gstNumber || orderDetails.gstNumber || '',
    address: activeOrder.shipping_address || activeOrder.address || orderDetails.address || '',
    due_date: activeOrder.created_at || new Date(),
    payment_status: paymentStatus || activeOrder.payment_status || 'Paid',
    items: rawItems.length > 0 ? rawItems.map((item, idx) => ({
      index: idx + 1,
      description: item.product_name || item.description || item.name || 'Software Genuine License',
      hsn_sac: item.hsn_sac || '997331',
      quantity: parseFloat(item.quantity || item.qty || 1),
      unit: item.unit || 'Pcs',
      unit_price: parseFloat(item.unit_price || item.unitPrice || item.price || 0),
      total_price: parseFloat(item.total_price || item.totalPrice || item.total || ((item.quantity || 1) * (item.price || 0))),
    })) : [
      {
        index: 1,
        description: 'Software Genuine Lifetime Retail License',
        hsn_sac: '997331',
        quantity: 1,
        unit: 'Pcs',
        unit_price: totalAmount || 990,
        total_price: totalAmount || 990,
      }
    ],
    total: totalAmount || 990,
    received: paymentStatus === 'Paid' ? (totalAmount || 990) : 0,
    balance: paymentStatus === 'Paid' ? 0 : (totalAmount || 990),
  };

  const recipientEmail = activeOrder.customer_email || activeOrder.customerEmail || orderDetails.email || '';

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 }, textAlign: 'center' }}>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <CheckCircleOutline sx={{ fontSize: { xs: 70, md: 90 }, color: 'success.main', mb: 2 }} />
      </motion.div>
      
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#1A237E', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
        Order Confirmed!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Thank you for your purchase. Your genuine digital license key is being processed.
      </Typography>

      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3.5, border: '1px solid #E2E8F0', borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            ORDER REFERENCE
          </Typography>
          {isVerifying ? (
            <Chip icon={<CircularProgress size={14} color="inherit" />} label="Verifying Payment..." size="small" variant="outlined" />
          ) : (
            <Chip 
              icon={<VerifiedUser sx={{ fontSize: '16px !important' }} />} 
              label={`Payment: ${paymentStatus}`} 
              color={paymentStatus === 'Paid' ? 'success' : 'primary'} 
              size="small" 
              sx={{ fontWeight: 700 }} 
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, my: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: 0.5, wordBreak: 'break-all' }}>
            {orderId}
          </Typography>
          <Button 
            size="small" 
            onClick={handleCopy} 
            sx={{ minWidth: 32, p: 0.5, color: copied ? 'success.main' : 'text.secondary' }}
          >
            {copied ? <Typography variant="caption" sx={{ fontWeight: 700 }}>COPIED!</Typography> : <ContentCopy sx={{ fontSize: 20 }} />}
          </Button>
        </Box>
        <Divider sx={{ my: 2.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, textAlign: 'left' }}>
          <Email color="primary" sx={{ fontSize: 30, mt: 0.5, flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>Instant Email Confirmation</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mb: 1 }}>
              We've dispatched your product license key, activation steps, and tax invoice{recipientEmail ? ` to ${recipientEmail}` : ' to your registered email'}.
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={resendingEmail ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 14 }} />}
              onClick={handleResendEmail}
              disabled={resendingEmail}
              sx={{ textTransform: 'none', fontWeight: 700, p: 0, fontSize: '12px', color: '#1565C0' }}
            >
              {resendingEmail ? 'Sending...' : 'Resend Confirmation Email'}
            </Button>
            {emailNotice && (
              <Alert severity={emailNotice.startsWith('✓') ? 'success' : 'info'} sx={{ mt: 1, py: 0.25, fontSize: '12px' }}>
                {emailNotice}
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<ReceiptLong />}
          onClick={() => setInvoiceOpen(true)}
          sx={{
            backgroundColor: PURPLE,
            '&:hover': { backgroundColor: '#6454DB' },
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            py: 1,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Download Tax Invoice
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/shop')}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 3, py: 1, width: { xs: '100%', sm: 'auto' } }}
        >
          Continue Shopping
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/account/track?orderId=${encodeURIComponent(orderId)}`)}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 3, py: 1, width: { xs: '100%', sm: 'auto' } }}
        >
          Check Tracking
        </Button>
      </Box>

      {/* Invoice Download Modal */}
      <InvoiceModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        data={invoiceData}
      />
    </Container>
  );
};

export default OrderSuccess;
